'use client';

import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';

import { IconArrowDown } from '@sympla/sympla-components';
import Breadcrumbs from '@sympla/sympla-components/Breadcrumbs';
import Modal from '@sympla/sympla-components/Modal';
import Button from '@sympla/sympla-components/Button';

import searchCategoryType from 'services/searchCategoryType/searchClient';
import { Matrix } from 'components/Matrix/Matrix';
import { Loading } from 'components/Loading';
import { StaticDateFilter } from 'components/StaticDateFilter/StaticDateFilter';
import EmptySearch from 'components/EmptySearch/EmptySearch';
import ListEventsCollection from 'components/ListEventsCollection/ListEventsCollection';
import BannerTopsort from 'components/BannerTopsort/BannerTopsort';
import SearchFilter from 'components/SearchFilter/SearchFilter';
import BannerTimer from 'components/BannerTimer/BannerTimer';
import LocationModal from 'components/Header/components/LocationModal/LocationModal';
import PromotionalBanner from 'components/PromotionalBanner/PromotionalBanner';

import { searchEventStore } from 'stores/SearchContentBaseStore';
import locationStore from 'stores/LocationStore';

import {
  ANYWHERE_LOCATION, LIMITS, MATRIX_TYPE, PAGES_URL, ROUTE_TYPE,
} from 'constants/index';
import {
  initialSearchDataResult, routeServices, templateSearch, urlParamsSearch,
} from 'constants/search';
import { PageType, SortType } from 'constants/enums/search';

import { MatrixComponent, Sort } from 'matrix.d';
import { SearchEventResult } from 'searchEvent';
import { Location } from 'location.d';
import { SearchCollection } from 'searchCollection';

import remoteConfigDiscovery from 'json/RemoteConfig.json';
import { RemoteConfig } from 'RemoteConfig.d';
import { ParamsMapSearch } from 'ParamsResult';

import { createLinkToRedirect } from 'utils/createLinkToRedirect';
import { browserLogger } from 'utils/logger/browserLogger';
import { getFullErrorObject } from 'utils/getErrorMessage';
import { convertQueryParamToObject } from 'utils/QueryParams/convertQueryParam';

import {
  ContainerCenter, ContainerMargin, TopicContainer,
} from 'components/Container/Container.css';

import {
  ContainerComponent,
  collectionContainerTitle,
  collectionTitle,
  collectionDescription,
  collectionContainerFilters,
  locationButton,
  collectionDescriptionTruncate,
  collectionDescriptionReadMore,
  ContainerBannerTopsort,
} from './CollectionSearch.css';

export type CollectionSearchSearchTemplateProps = {
  components: MatrixComponent[];
  template: string;
  uuid: string;
  label: string;
  description: string;
  componentTotal: number;
  matrixSlug?: string;
  matrixType?: string;
  isMobile: boolean;
  dataSectionMoreEvents?: SearchEventResult;
  svDataSectionMoreEvents?: ParamsMapSearch;
  collections?: SearchCollection;
  location: Location;
};

function CollectionSearchTemplate({
  components,
  label,
  description,
  template,
  uuid,
  componentTotal,
  isMobile,
  matrixType,
  matrixSlug = '',
  dataSectionMoreEvents,
  svDataSectionMoreEvents,
  collections,
  location,
}: CollectionSearchSearchTemplateProps) {
  searchEventStore.setTypePage(PageType.COLLECTION);

  const router = useRouter();

  const [newComponents, setNewComponents] = useState<MatrixComponent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFilter, setIsLoadingFilter] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isEmptyStateRenderable, setEmptyStateRenderable] = useState(false);
  const [isNotEvent, setIsNotEvent] = useState(false);
  const [moreEvents, setMoreEvents] = useState(dataSectionMoreEvents);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const isCityCollection = template === templateSearch.city;
  const hasStaticDate = (remoteConfigDiscovery as RemoteConfig).discovery?.collection?.[matrixSlug]?.staticFilterDate?.status ?? false;
  const hasMoreEventOption = (remoteConfigDiscovery as RemoteConfig).discovery?.collection?.[matrixSlug]?.hasMoreEventOption?.status ?? true;
  const hasTimer = (remoteConfigDiscovery as RemoteConfig).discovery?.collection?.[matrixSlug]?.timerCollection?.status ?? false;
  const hasPromotionalBanner = (remoteConfigDiscovery as RemoteConfig).discovery?.collection?.[matrixSlug]?.promotionalBanner?.status ?? false;
  const dataCollection = collections?.matrices?.find((matrix) => matrixSlug?.includes(matrix.slug));
  const slotIdBannerTopsort = dataCollection?.topsort_slot_id;

  const handleErrorFetch = useCallback((error: Error) => {
    const logger = browserLogger();
    logger.init();
    const err = getFullErrorObject<Error>(error);
    logger.error(
      {
        message: 'Uncaught error: loading elements from the Topic page',
        data: err.message || 'searchCategoryType error',
        extra: { error },
      },
      'fn searchCategoryType()',
    );
  }, []);

  const commonsQuery = useCallback(
    (query: string, sort?: string) => {
      const queryParams = convertQueryParamToObject(query);
      return {
        location: isEmptyStateRenderable ? urlParamsSearch.ignoreLocation : '',
        d: String(queryParams?.d || ''),
        p: String(queryParams?.p || ''),
        sort: String((queryParams?.ordem) || sort || SortType.GLOBAL_SCORE) as Sort,
      };
    },
    [isEmptyStateRenderable],
  );

  const loadMoreComponents = async (query: string, index: number) => {
    if (!components[index]) {
      setIsLoading(false);
      if (!newComponents.length) setIsNotEvent(true);
      return;
    }
    if (newComponents.length >= componentTotal) return;
    setIsLoading(true);

    try {
      const { service, service_params } = components[index];
      const params = { ...service_params };
      if (isEmptyStateRenderable) delete params?.location;

      const data = await searchCategoryType({
        service,
        ...params,
        ...commonsQuery(query, params?.sort),
      });
      const matrix = { ...components[index], response: data || initialSearchDataResult };
      setNewComponents((prevComponents) => [...prevComponents, matrix]);

      if (newComponents.length + 1 >= componentTotal) {
        setIsLoading(false);
      }
    } catch (error) {
      handleErrorFetch(error as Error);
    }
  };

  const fecthSectionMoreEvents = async (query: string, isChangePage?: boolean) => {
    try {
      const queryParams = convertQueryParamToObject(query);
      if (queryParams?.ordem) queryParams.sort = queryParams.ordem;

      const searchParamsObject = {
        ...svDataSectionMoreEvents,
        has_banner: 1,
        ...queryParams,
        page: Number(queryParams?.page || 1),
        limit: LIMITS.TRACK_MORE_EVENTS,
        service: routeServices.search,
      };

      const result = await searchCategoryType(searchParamsObject);

      if (result) {
        const data = moreEvents?.data || [];
        const mobilePayload = {
          ...result,
          data: [...data, ...result.data],
        };

        const newValue = isMobile && isChangePage ? mobilePayload : result;
        setMoreEvents(newValue);
      }
    } catch (error) {
      handleErrorFetch(error as Error);
    } finally {
      setIsLoadingPage(false);
      setIsLoadingFilter(false);
    }
  };

  const changeFilter = async (query: string) => {
    setEmptyStateRenderable(false);
    setIsNotEvent(false);
    setNewComponents([]);
    if (hasMoreEventOption && dataSectionMoreEvents?.total) {
      setIsLoadingFilter(true);
      fecthSectionMoreEvents(query);
    }
  };

  useEffect(() => {
    if (newComponents.length < componentTotal) {
      loadMoreComponents(window.location.search, newComponents.length);
    }
    if (newComponents.length >= componentTotal) {
      setIsLoading(false);
    }
  }, [newComponents.length]);

  useEffect(() => {
    const noEventsOnArray = newComponents?.every((component) => component?.response?.data?.length === 0);
    const noMoreEvents = hasMoreEventOption ? !moreEvents?.data?.length : true;
    if (noEventsOnArray && noMoreEvents) {
      const isGetNewMatrixes = !isEmptyStateRenderable && locationStore?.selectedLocation?.city !== ANYWHERE_LOCATION;
      if (newComponents.length >= componentTotal && isGetNewMatrixes) {
        setEmptyStateRenderable(true);
        setNewComponents([]);
      }
      if (newComponents.length >= componentTotal && !isGetNewMatrixes) {
        setIsNotEvent(true);
      }
    }
  }, [newComponents.length]);

  const categoryValue = template === templateSearch.collectionMatrix ? matrixSlug : '';
  const locationHref = useMemo(() => createLinkToRedirect({
    item: {
      urn: '',
      category: categoryValue,
    },
    param: '',
    routeType: ROUTE_TYPE.SEARCH_BAR_DEFAULT_FILTER,
    userLocation: locationStore?.selectedLocation,
    isAllEvents: true,
  }), [locationStore?.selectedLocation]);

  const breadcrumbCollection = [
    { label: 'Home', link: PAGES_URL.home },
    { label: 'Coleções', link: PAGES_URL.categories },
    { label, link: `${PAGES_URL.events}/${matrixSlug}`, active: true },
  ];

  const BannerTopsortComponent = slotIdBannerTopsort && (
    <div className={ContainerBannerTopsort}>
      <BannerTopsort isMobile={isMobile} idBanner={slotIdBannerTopsort} />
    </div>
  );

  return (
    <div className={TopicContainer}>
      <div className={ContainerComponent}>
        <Breadcrumbs list={breadcrumbCollection} />
      </div>
      {hasTimer && (
        <div className={ContainerComponent}>
          <BannerTimer matrixSlug={matrixSlug} label={label} />
        </div>
      )}

      {hasPromotionalBanner && (
        <div className={ContainerComponent}>
          <PromotionalBanner matrixSlug={matrixSlug} />
        </div>
      )}

      <div className={collectionContainerTitle}>
        <h1 className={collectionTitle}>
          {isCityCollection ? 'O que fazer ' : label}
          {' '}
          em
          {' '}
          <button
            type="button"
            className={locationButton}
            onClick={() => locationStore.setIsLocationModalVisible(true)}
          >
            {isCityCollection
              ? label
              : locationStore?.selectedLocation?.city || location.city || ANYWHERE_LOCATION}
            <IconArrowDown width={24} height={24} />
          </button>
        </h1>
        {description && (
        <div>
          <p
            className={
              `${collectionDescription} `
              + `${isMobile && !isDescriptionExpanded ? collectionDescriptionTruncate : ''}`
            }
          >
            {description}
          </p>
          {isMobile && (
            <button
              type="button"
              className={collectionDescriptionReadMore}
              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
            >
              {isDescriptionExpanded ? 'Ler menos' : 'Ler mais'}
            </button>
          )}
        </div>
        )}
      </div>

      <div className={collectionContainerFilters}>
        <SearchFilter
          fetchData={changeFilter}
          isSpaceBetween
          activeFilters={{
            collections: false,
            price: true,
            date: true,
            type: false,
          }}
        />
      </div>
      {isEmptyStateRenderable && !isNotEvent && newComponents.length && (
      <div className={ContainerMargin}>
        <EmptySearch hasButtonChangeLocation hasOtherResult hasMargin={false} />
      </div>
      )}
      {isNotEvent && (
      <div className={ContainerMargin}>
        <EmptySearch hasMargin={false} />
      </div>
      )}

      <div>
        {newComponents.length ? (
          <Matrix
            components={newComponents}
            matrixType={matrixType ?? MATRIX_TYPE.HOME_MATRIX}
            prefixTrackingName={MATRIX_TYPE.MATRIX}
            matrixUuid={uuid}
            cardPreviousView={{ recents: [] }}
            userLocation={locationStore?.selectedLocation}
            isCollectionSearchVariant={false}
            bannerTopsort={slotIdBannerTopsort ? { id: slotIdBannerTopsort, positionIndex: 0 } : undefined}
            isMobile={isMobile}
          />
        ) : BannerTopsortComponent}
      </div>
      {isLoading && <Loading isNotFullScreen />}

      {hasMoreEventOption && moreEvents?.data?.length && !isLoadingFilter ? (
        <ListEventsCollection
          dataSectionMoreEvents={moreEvents}
          matrixUuid={uuid}
          isMobile={isMobile}
          fecthEvents={(query: string) => fecthSectionMoreEvents(query, true)}
          isLoading={isLoadingPage}
          label={label}
          isCityCollection={isCityCollection}
        />
      ) : null}

      {hasStaticDate && (
      <div className={ContainerComponent}>
        <StaticDateFilter categoryName={template === templateSearch.collectionMatrix ? label : ''} categoryValue={categoryValue} />
        <div className={ContainerCenter}>
          <Button
            upperCase={false}
            size="small"
            onClick={() => {
              router.push(locationHref);
            }}
          >
            {`Ver todos os eventos  ${template === templateSearch.city ? 'em' : 'de'}  ${label}`}
          </Button>
        </div>
      </div>
      )}

      <Modal
        title={false}
        width={520}
        open={locationStore.isLocationModalVisible}
        onClose={() => locationStore.setIsLocationModalVisible(false)}
        accessibilityTitle="Modal de Localização"
      >
        <LocationModal />
      </Modal>
    </div>
  );
}

export default observer(CollectionSearchTemplate);
